import { getLocaleUnit } from 'careit';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useAuth from './hooks/useAuth';
import { BUCKET_NAME, S3_BASE_URL } from './config';
import i18n from 'src/locales/i18n';

export const getS3Url = (url) => {
  return `${S3_BASE_URL}/${BUCKET_NAME}/${url}`;
};

/**
 * Prefer `useFoodTypes` which handles localization.
 * @deprecated
 */
export const FOOD_TYPES = [
  { title: 'Dairy', value: 'DAIRY', key: 'dairy' },
  { title: 'Meat', value: 'MEAT', key: 'meat' },
  { title: 'Produce', value: 'PRODUCE', key: 'produce' },
  { title: 'Prepared', value: 'PREPARED', key: 'prepared' },
  { title: 'Non-Perishable', value: 'NON-PERISHABLE', key: 'non_perishable' },
  { title: 'Bakery', value: 'BAKED', key: 'baked' },
  { title: 'Beverages', value: 'BEVERAGES', key: 'beverages' },
  { title: 'Frozen', value: 'FROZEN', key: 'frozen' },
  { title: 'Non-Food', value: 'NON-FOOD', key: 'non_food' },
  { title: 'Compost', value: 'COMPOST', key: 'compost' },
  { title: 'Unusable', value: 'UNUSABLE', key: 'unusable' },
  { title: 'Animal Feed', value: 'ANIMAL-FEED', key: 'animal_feed' },
];

export function useFoodTypes() {
  const { t, i18n } = useTranslation('translation');

  return useMemo(
    () => [
      {
        title: t('data.food-types.dairy'),
        value: 'DAIRY',
        key: 'dairy',
      },
      {
        title: t('data.food-types.meat'),
        value: 'MEAT',
        key: 'meat',
      },
      {
        title: t('data.food-types.produce'),
        value: 'PRODUCE',
        key: 'produce',
      },
      {
        title: t('data.food-types.prepared'),
        value: 'PREPARED',
        key: 'prepared',
      },
      {
        title: t('data.food-types.non-perishable'),
        value: 'NON-PERISHABLE',
        key: 'non_perishable',
      },
      {
        title: t('data.food-types.baked'),
        value: 'BAKED',
        key: 'baked',
      },
      {
        title: t('data.food-types.beverages'),
        value: 'BEVERAGES',
        key: 'beverages',
      },
      {
        title: t('data.food-types.frozen'),
        value: 'FROZEN',
        key: 'frozen',
      },
      {
        title: t('data.food-types.non-food'),
        value: 'NON-FOOD',
        key: 'non_food',
      },
      {
        title: t('data.food-types.compost'),
        value: 'COMPOST',
        key: 'compost',
      },
      {
        title: t('data.food-types.unusable'),
        value: 'UNUSABLE',
        key: 'unusable',
      },
      {
        title: t('data.food-types.animal-feed'),
        value: 'ANIMAL-FEED',
        key: 'animal_feed',
      },
    ],
    [i18n.language]
  );
}

export const roles = {
  admin: 'ADMIN',
  staff: 'STAFF',
  driver: 'DRIVER',
};

export const RESCUE_STATUSES = {
  ASSIGNED: 'ASSIGNED',
  AWAITING_DRIVER: 'AWAITING_DRIVER',
  CANCELLED: 'CANCELLED',
  DELIVERY_IN_PROGRESS: 'DELIVERY_IN_PROGRESS',
  INCOMPLETE: 'INCOMPLETE',
  PICKUP_IN_PROGRESS: 'PICKUP_IN_PROGRESS',
  STARTED: 'STARTED',
  WAITING_FOR_APPROVAL: 'WAITING_FOR_APPROVAL',
  WAITING_FOR_REASSIGN: 'WAITING_FOR_REASSIGN',
};

// Getters translate on access (at render time), never at module load.
export const adornment = {
  canada: {
    get title() {
      return i18n.t(
        'auth.sign-up.about-business-form.ein.canada.business-title'
      );
    },
    get description() {
      return i18n.t(
        'auth.sign-up.about-business-form.ein.canada.business-description'
      );
    },
  },
  usa: {
    get title() {
      return i18n.t('auth.sign-up.about-business-form.ein.us.title');
    },
    get description() {
      return i18n.t('auth.mobile.signup.ein-description');
    },
  },
};

// `label` is a getter so it is translated when rendered, not at module load.
const vehicleOption = (value, labelKey) => ({
  value,
  labelKey,
  get label() {
    return i18n.t(labelKey);
  },
});

export const VEHICLES = [
  vehicleOption('NO_VEHICLE', 'donations.no-vehicle'),
  vehicleOption('CAR', 'donations.car'),
  vehicleOption('VAN', 'donations.van'),
  vehicleOption('TRUCK', 'donations.truck'),
];

export const DEFAULT_FILTER_MODEL = {
  items: [],
  linkOperator: 'and',
};

export const ONBOARDING_REQUIRED_ERORR =
  'You must complete required onboarding items before rescuing from this store.';

export const ALREADY_CLAIMED_ERROR =
  'This donation is already claimed by someone else.';

export const useGetVehicleLabels = () => {
  const { t } = useTranslation('translation');
  const { user } = useAuth();

  let unit = 'lbs';
  if (user) {
    const { weight } = getLocaleUnit(user);
    unit = weight;
  }

  const carLabel = `${t('donations.car')} (${
    unit === 'lbs' ? '20-200 lbs' : '10-100 kgs'
  })`;
  const vanLabel = `${t('donations.van')} (${
    unit === 'lbs' ? '201-2000 lbs' : '101-1000 kgs'
  })`;
  const truckLabel = `${t('donations.truck')} (${
    unit === 'lbs' ? '2000+ lbs' : '1000+ kgs'
  })`;
  return [
    {
      value: 0,
      title: carLabel,
      label: carLabel,
      vehicle: 'CAR',
    },
    {
      value: 1,
      label: vanLabel,
      title: vanLabel,
      vehicle: 'VAN',
    },
    {
      value: 2,
      label: truckLabel,
      title: truckLabel,
      vehicle: 'TRUCK',
    },
  ];
};
